import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { noAutoClose, toastConfig } from 'assets/data/config';
import StylishViewFileDialog from 'components/DesignSystems/New/StylishViewFileDialog';
import { exportedFormFilesKeys } from 'components/FileManagement/hooks/useExportedFormFiles';
import { filesOrFoldersKeys } from 'components/FileManagement/hooks/useGetAllFiles';
import {
  ICS_204_CG_FORM_NAME,
  postExportWorkAssignmentListAttachment,
} from 'components/IAP/hooks/assignmentListHooks';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import {
  useCurrentPeriod,
  useIncident,
} from 'components/IAP/hooks/incidentHooks';
import {
  ExportPlannedResourceAsFormParams,
  postExportPlannedResourceAsForm,
} from 'components/IAP/hooks/workAssignmentListAttachmentHooks';
import {
  ExportAsFormParams,
  postExportAsForm,
  postPersistIcsFormExport,
} from 'hooks/incident/useExportForm';
import { postExportPaginatedIap } from 'hooks/incident/useExportPaginatedIAP';
import {
  selectGroupGuid,
  selectUser,
  useAppSelector,
} from 'slices/commonSelectors';
import Sentry from '../../../instrument';
import downloadFile from 'utils/downloadFile';
import { base64PdfToObjectUrl } from 'utils/pdfPreview';
import { useSearchParams } from 'react-router';

type PreviewDownloadMode = 'base64' | 'signed' | 'none';

type IcsFormPdfPersistMeta = {
  workspaceFormname: string;
  periodNumber?: number | null;
};

type Base64PreviewOpenOptions = {
  allowWorkspaceDownload?: boolean;
  persistMeta: IcsFormPdfPersistMeta;
};

type IcsFormPdfPreviewContextValue = {
  exportAsForm: (
    params: ExportAsFormParams,
    options?: { onSuccess?: () => void; allowWorkspaceDownload?: boolean }
  ) => Promise<void>;
  exportPlannedResourceAsForm: (
    params: ExportPlannedResourceAsFormParams
  ) => Promise<void>;
  exportWorkAssignmentListAttachment: (walaId: string) => Promise<void>;
  exportPaginatedIAP: ({
    mapSketch,
    periodNumber,
  }: {
    mapSketch?: File;
    periodNumber?: number;
  }) => Promise<void>;
  /** Preview a PDF from an already-resolved URL (e.g. signed blob URL). */
  previewFromSignedUrl: (args: {
    fileUrl: string;
    fileName: string | null;
    onDownload: () => void | Promise<void>;
  }) => void;
  isExporting: boolean;
};

const IcsFormPdfPreviewContext =
  createContext<IcsFormPdfPreviewContextValue | null>(null);

function toastExportError(error: unknown, label: string) {
  const err = error as {
    response?: { data?: { ERROR?: string; message?: string } };
    message?: string;
  };
  if (err?.response?.data?.ERROR === 'UNAUTHORIZED') {
    toast.error('You are not authorized to perform this action.');
    return;
  }
  if (err?.response?.data?.message) {
    toast.error(err.response.data.message);
    return;
  }
  const errorMessage =
    `${label}\n\n` +
    ((err?.message && typeof err.message === 'string' && err.message) || '') +
    '\n\nIf you continue to experience this error, contact support@disastertech.com';
  toast.error(errorMessage, noAutoClose);
}

export function IcsFormPdfPreviewProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const queryClient = useQueryClient();
  const { data: incident } = useIncident();
  const user = useAppSelector(selectUser);
  const groupGuid = useAppSelector(selectGroupGuid);
  const { periodNumber } = useCurrentPeriod();
  const incidentId = useActualIncidentId();
  const [show, setShow] = useState(false);
  const [fileUrl, setFileUrl] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [downloadMode, setDownloadMode] = useState<PreviewDownloadMode>('none');
  const [searchParams] = useSearchParams();

  const revokeObjectUrlRef = useRef<(() => void) | null>(null);
  const downloadFromBase64Ref = useRef<{
    stem: string;
    base64: string;
    allowWorkspaceDownload: boolean;
    persistMeta: IcsFormPdfPersistMeta;
  } | null>(null);
  const signedDownloadRef = useRef<(() => void | Promise<void>) | null>(null);

  const [exportInFlight, setExportInFlight] = useState(0);
  const beginExport = useCallback(() => {
    setExportInFlight((n) => n + 1);
  }, []);
  const endExport = useCallback(() => {
    setExportInFlight((n) => Math.max(0, n - 1));
  }, []);

  const clearBlobPreview = useCallback(() => {
    revokeObjectUrlRef.current?.();
    revokeObjectUrlRef.current = null;
    downloadFromBase64Ref.current = null;
    signedDownloadRef.current = null;
    setDownloadMode('none');
  }, []);

  const closeModal = useCallback(() => {
    clearBlobPreview();
    setShow(false);
    setFileUrl('');
    setFileName(null);
  }, [clearBlobPreview]);

  const openBase64Preview = useCallback(
    (
      base64: string,
      modalTitle: string,
      downloadStem: string,
      openOptions: Base64PreviewOpenOptions
    ) => {
      clearBlobPreview();
      try {
        const { objectUrl, revoke } = base64PdfToObjectUrl(base64);
        revokeObjectUrlRef.current = revoke;
        downloadFromBase64Ref.current = {
          stem: downloadStem,
          base64,
          allowWorkspaceDownload: openOptions.allowWorkspaceDownload !== false,
          persistMeta: openOptions.persistMeta,
        };
        setDownloadMode('base64');
        setFileUrl(objectUrl);
        setFileName(modalTitle);
        setShow(true);
      } catch (e) {
        toast.error('Error preparing PDF preview.');
        Sentry.captureException(e);
      }
    },
    [clearBlobPreview]
  );

  const previewFromSignedUrl = useCallback(
    (args: {
      fileUrl: string;
      fileName: string | null;
      onDownload: () => void | Promise<void>;
    }) => {
      clearBlobPreview();
      signedDownloadRef.current = args.onDownload;
      setDownloadMode('signed');
      setFileUrl(args.fileUrl);
      setFileName(args.fileName);
      setShow(true);
    },
    [clearBlobPreview]
  );

  const handleHeaderDownload = useCallback(async () => {
    if (downloadMode === 'base64' && downloadFromBase64Ref.current) {
      const { stem, base64, allowWorkspaceDownload, persistMeta } =
        downloadFromBase64Ref.current;
      if (!allowWorkspaceDownload) {
        return;
      }
      try {
        downloadFile(stem, 'PDF', base64);
        await postPersistIcsFormExport(
          {
            incident,
            user,
            groupGuid,
          },
          {
            pdfBase64: base64,
            workspaceFormname: persistMeta.workspaceFormname,
            periodNumber: persistMeta.periodNumber,
          }
        );

        if (incident?.id) {
          await queryClient.refetchQueries({
            queryKey: exportedFormFilesKeys.byIncident(incident.id),
          });
          await queryClient.refetchQueries({
            queryKey: filesOrFoldersKeys.allFiles(incident.id),
          });
        }
        toast.success('PDF downloaded and updated');
      } catch (e) {
        toast.error('Error downloading or saving the form.');
        Sentry.captureException(e);
      }
      return;
    }
    if (downloadMode === 'signed' && signedDownloadRef.current) {
      void signedDownloadRef.current();
    }
  }, [downloadMode, groupGuid, incident, queryClient, user]);

  const exportAsForm = useCallback(
    async (
      params: ExportAsFormParams,
      options?: { onSuccess?: () => void; allowWorkspaceDownload?: boolean }
    ) => {
      beginExport();
      try {
        // By default, the "Future Period" tab does not use an operational period
        // number — it uses ics_incident_info timestamp values instead. Callers
        // that need to override this (e.g. ICS 209, which always references the
        // current period regardless of tab) can pass params.periodNumber
        // explicitly and it will take precedence.
        const mode = searchParams.get('mode');
        const effectivePeriodNumber = (() => {
          if (params.periodNumber != null) return params.periodNumber;
          if (mode === 'future') return undefined;
          return periodNumber;
        })();

        const result = await postExportAsForm(
          {
            incident,
            user,
            groupGuid,
          },
          {
            ...params,
            periodNumber: effectivePeriodNumber,
          }
        );
        const title = `${result.formname.replace(/_/g, '-')}.pdf`;

        openBase64Preview(result.data, title, result.formname, {
          allowWorkspaceDownload: options?.allowWorkspaceDownload !== false,
          persistMeta: {
            workspaceFormname: result.formname,
            periodNumber: effectivePeriodNumber,
          },
        });
        options?.onSuccess?.();
      } catch (error) {
        toastExportError(error, 'exportAsFormError');
      } finally {
        endExport();
      }
    },
    [
      beginExport,
      endExport,
      groupGuid,
      incident,
      openBase64Preview,
      periodNumber,
      user,
      searchParams,
    ]
  );

  const exportPlannedResourceAsForm = useCallback(
    async (params: ExportPlannedResourceAsFormParams) => {
      beginExport();
      try {
        const result = await postExportPlannedResourceAsForm(
          periodNumber,
          params
        );
        const title = `${result.formName.replace(/_/g, '-')}.pdf`;
        openBase64Preview(result.data, title, result.formName, {
          persistMeta: {
            workspaceFormname: result.formName,
            periodNumber,
          },
        });
      } catch (error) {
        toastExportError(error, 'exportAsFormError');
      } finally {
        endExport();
      }
    },
    [beginExport, endExport, openBase64Preview, periodNumber]
  );

  const exportWorkAssignmentListAttachment = useCallback(
    async (walaId: string) => {
      beginExport();
      try {
        const data = await postExportWorkAssignmentListAttachment(
          incidentId,
          periodNumber,
          walaId
        );
        const title = `${ICS_204_CG_FORM_NAME.replace(/_/g, '-')}.pdf`;
        openBase64Preview(data, title, ICS_204_CG_FORM_NAME, {
          persistMeta: {
            workspaceFormname: ICS_204_CG_FORM_NAME,
            periodNumber,
          },
        });
      } catch (error: unknown) {
        const err = error as { message?: string };
        toast.error(err?.message || 'Export failed', toastConfig);
      } finally {
        endExport();
      }
    },
    [beginExport, endExport, incidentId, openBase64Preview, periodNumber]
  );

  const exportPaginatedIAP = useCallback(
    async ({
      mapSketch,
      periodNumber: callerPeriodNumber,
    }: {
      mapSketch?: File;
      periodNumber?: number;
    }) => {
      beginExport();
      try {
        const mode = searchParams.get('mode');
        const isFuture = mode === 'future';

        // Tag (filename prefix IAP-OP{N}-) always uses the target period number
        // so the inline viewer's regex lookup matches.
        const tagPeriodNumber = (() => {
          if (callerPeriodNumber != null) return callerPeriodNumber;
          if (isFuture && periodNumber != null) return periodNumber + 1;
          return periodNumber;
        })();

        // Backend generation: in future mode the next period has no DB row yet,
        // so omit periodNumber — the backend falls back to ics_incident_info
        // next-period timestamps.
        const backendPeriodNumber = isFuture ? undefined : tagPeriodNumber;

        const formnameStem = `${(incident as { name?: string })?.name ?? 'Incident'} IAP`;
        const result = await postExportPaginatedIap(
          {
            incident,
            user,
            groupGuid,
            formnameStem,
            mapSketch,
          },
          backendPeriodNumber
        );
        const title = `${formnameStem}.pdf`;
        openBase64Preview(result.data, title, result.downloadStem, {
          persistMeta: {
            workspaceFormname: 'IAP',
            periodNumber: tagPeriodNumber,
          },
        });
      } catch (error) {
        toastExportError(error, 'exportPaginatedIAPError');
      } finally {
        endExport();
      }
    },
    [
      beginExport,
      endExport,
      groupGuid,
      incident,
      openBase64Preview,
      periodNumber,
      searchParams,
      user,
    ]
  );

  const value = useMemo(
    () => ({
      exportAsForm,
      exportPlannedResourceAsForm,
      exportWorkAssignmentListAttachment,
      exportPaginatedIAP,
      previewFromSignedUrl,
      isExporting: exportInFlight > 0,
    }),
    [
      exportAsForm,
      exportInFlight,
      exportPaginatedIAP,
      exportPlannedResourceAsForm,
      exportWorkAssignmentListAttachment,
      previewFromSignedUrl,
    ]
  );

  const base64AllowsHeaderDownload =
    downloadMode === 'base64' &&
    downloadFromBase64Ref.current?.allowWorkspaceDownload !== false;
  const showHeaderDownload =
    downloadMode === 'signed' || base64AllowsHeaderDownload;

  return (
    <IcsFormPdfPreviewContext.Provider value={value}>
      {children}
      <StylishViewFileDialog
        show={show}
        onClose={closeModal}
        fileUrl={fileUrl}
        fileName={fileName}
        onDownload={
          showHeaderDownload
            ? () => {
                void handleHeaderDownload();
              }
            : undefined
        }
        downloadLabel="Download"
        mimetype="application/pdf"
      />
    </IcsFormPdfPreviewContext.Provider>
  );
}

export function useIcsFormPdfPreview(): IcsFormPdfPreviewContextValue {
  const ctx = useContext(IcsFormPdfPreviewContext);
  if (!ctx) {
    throw new Error(
      'useIcsFormPdfPreview must be used within IcsFormPdfPreviewProvider'
    );
  }
  return ctx;
}

/**
 * Returns the IcsFormPdfPreview context value, or `null` when rendered outside
 * `IcsFormPdfPreviewProvider` (e.g. MapPage on `/` or `/hub`).
 *
 * Intentionally does NOT use the throw-pattern because some routes render
 * resource components without the ICS provider. Callers must null-check before
 * using the returned value.
 */
export function useOptionalIcsFormPdfPreview(): IcsFormPdfPreviewContextValue | null {
  return useContext(IcsFormPdfPreviewContext);
}
